import {
  buildCronFromFrequency,
  Frequency,
  type FrequencyState,
  getDefaultFrequencyState,
  isValidHourCron,
  parseCronToFrequency,
} from '../shared/schedule.helpers';

type ScheduleBuilderHydratedState = {
  frequencyState: FrequencyState;
  isAdvanced: boolean;
  customCron: string;
};

/**
 * Derives the editor's local frequency / advanced-cron state from a persisted cron expression.
 *
 * Responsibilities:
 * 1. Default to a fresh frequency state when no cron is persisted (new workflow)
 * 2. Try to parse the cron back into a frequency preset when possible
 * 3. Fall back to advanced cron mode (raw text input) when the cron isn't expressible as a preset
 *
 * @param {string | null} cronExpression - The persisted cron expression, or null for a fresh form
 * @returns {ScheduleBuilderHydratedState} Initial frequency state, isAdvanced flag, and customCron string
 */
function hydrateScheduleState(cronExpression: string | null): ScheduleBuilderHydratedState {
  const defaultState = getDefaultFrequencyState();
  if (!cronExpression) {
    return { frequencyState: defaultState, isAdvanced: false, customCron: '' };
  }
  const parsed = parseCronToFrequency(cronExpression, defaultState.timezone);
  if (parsed) {
    return { frequencyState: parsed, isAdvanced: false, customCron: '' };
  }
  return { frequencyState: defaultState, isAdvanced: true, customCron: cronExpression };
}

/**
 * Computes the effective cron string from the builder's internal state, returning null when incomplete (e.g. weekly with no days selected, or empty custom cron).
 *
 * @param {FrequencyState} frequencyState - Current frequency picker state
 * @param {boolean} isAdvanced - True when the user is in custom-cron mode
 * @param {string} customCron - The raw custom cron string
 * @returns {string | null} The cron expression to send, or null when the form is incomplete
 */
function computeEffectiveCron(frequencyState: FrequencyState, isAdvanced: boolean, customCron: string): string | null {
  if (isAdvanced) return customCron.trim() || null;
  if (frequencyState.frequency === Frequency.WEEKLY && frequencyState.daysOfWeek.length === 0) {
    return null;
  }
  return buildCronFromFrequency(frequencyState);
}

/**
 * Reports whether the current builder state will produce a valid cron expression.
 *
 * @param {string | null} effectiveCron - Result of computeEffectiveCron
 * @param {boolean} isAdvanced - True when the user is in custom-cron mode
 * @returns {boolean} True when effectiveCron is non-null and (in advanced mode) is a valid hour-cron
 */
function isScheduleStateValid(effectiveCron: string | null, isAdvanced: boolean): boolean {
  if (!effectiveCron) return false;
  if (isAdvanced && !isValidHourCron(effectiveCron)) return false;
  return true;
}

export { computeEffectiveCron, hydrateScheduleState, isScheduleStateValid };
